// Business day: the one function behind every "which day does this belong to" question (doc 03, core plan §4.8).
//
// Definition. Business date D starts at start(D): the instant the location's wall clock first reads
// (D, startTime(D)), where startTime(D) is the `business_day.start_time` in effect for D. It ends where D + 1 starts,
// so business days tile time with no gaps or overlaps. An instant's business date is the D with
// start(D) <= instant < start(D + 1), which is its local calendar date or the day before.
//
// DST (CD-B05). Days are 23 or 25 hours across transitions. A start time inside a spring-forward gap begins the day at
// the first valid instant after the gap; inside a fall-back overlap it begins at the first occurrence. Comparison is by
// instant, not wall clock, so with a start inside the repeated hour the second pass through that hour belongs to the
// new day (e.g. start 01:30 on a fall-back Sunday: 01:15 PST, which follows 01:30 PDT, is already Sunday).
//
// Effective dating. `startTimeHistory` keeps every value with the business date it took effect; resolution picks the
// value in effect for each date, so a past instant always maps to the date it had when it happened. Changes made
// through `scheduleStartTimeChange` take effect from the next business day, never retroactively.
import { addDays, formatLocalDate, localDateToDays, localTimeToSeconds } from './calendar.js';
import { resolveEffective } from './effective-dated.js';
import { resolveLocal, toEpochMs, toLocal, type IanaZone } from './zone.js';
import { err, ok, type BusinessDate, type EffectiveDated, type InstantInput, type LocalDate, type LocalTime, type Result } from './types.js';

/** Legacy behavior and the registry default for `business_day.start_time`. */
export const DEFAULT_BUSINESS_DAY_START = '04:00' as LocalTime;

export type BusinessDayLocation = {
  readonly zone: IanaZone;
  readonly startTimeHistory: readonly EffectiveDated<LocalTime>[];
};

/** The start time in effect for a business date. */
export function startTimeFor(date: BusinessDate, loc: BusinessDayLocation): LocalTime {
  return resolveEffective(loc.startTimeHistory, date, DEFAULT_BUSINESS_DAY_START);
}

function startMsOfDays(days: number, loc: BusinessDayLocation): number {
  const start = startTimeFor(formatLocalDate(days), loc);
  return resolveLocal(loc.zone, days, localTimeToSeconds(start));
}

/** Epoch ms at which business date `date` begins. */
export function startOfBusinessDay(date: BusinessDate, loc: BusinessDayLocation): bigint {
  return BigInt(startMsOfDays(localDateToDays(date), loc));
}

export function businessDayOfMs(epochMs: number, loc: BusinessDayLocation): number {
  const calendarDays = toLocal(loc.zone, epochMs).days;
  return epochMs >= startMsOfDays(calendarDays, loc) ? calendarDays : calendarDays - 1;
}

/** The business date an instant belongs to. */
export function businessDay(at: InstantInput, loc: BusinessDayLocation): BusinessDate {
  return formatLocalDate(businessDayOfMs(toEpochMs(at), loc));
}

/** Half-open [start, end) epoch-ms range of a business date; 23 or 25 hours on DST days, other lengths on change days. */
export function businessDayRange(date: BusinessDate, loc: BusinessDayLocation): { start: bigint; end: bigint } {
  const days = localDateToDays(date);
  return { start: BigInt(startMsOfDays(days, loc)), end: BigInt(startMsOfDays(days + 1, loc)) };
}

/** Epoch ms of the first business-day start strictly after `at` (auto-close, number reset, KDS rollover). */
export function nextStartOfDay(at: InstantInput, loc: BusinessDayLocation): bigint {
  return BigInt(startMsOfDays(businessDayOfMs(toEpochMs(at), loc) + 1, loc));
}

/**
 * Records a new start time, effective from the next business day that has not begun under either value: normally the
 * business date after the one in progress at `now`, but one day later when the new value would have started that day
 * already (e.g. at 01:00 during Friday's 04:00 day, a change to 00:00 cannot take Saturday, which would have begun an
 * hour ago, so it takes Sunday). Pending changes (effective after the current business date) are replaced; entries in
 * effect today or earlier are never touched, and a deferred change pins tomorrow's previous start, so the business day
 * in progress keeps its end and no instant up to `now` changes its business date.
 */
export function scheduleStartTimeChange(
  loc: BusinessDayLocation,
  value: LocalTime,
  now: InstantInput,
): Result<{ history: EffectiveDated<LocalTime>[]; effectiveFrom: BusinessDate }, 'not_15_min_step'> {
  if (localTimeToSeconds(value) % 900 !== 0) return err('not_15_min_step');
  const nowMs = toEpochMs(now);
  const todayDays = businessDayOfMs(nowMs, loc);
  const today = formatLocalDate(todayDays);
  const nextStartUnderNew = resolveLocal(loc.zone, todayDays + 1, localTimeToSeconds(value));
  const deferred = nextStartUnderNew <= nowMs;
  const effectiveFrom = formatLocalDate(todayDays + (deferred ? 2 : 1));
  const kept = loc.startTimeHistory.filter((e) => e.effectiveFrom <= today);
  // When no entry is in effect yet, pin today's resolved value so dropping pending entries cannot change past dates.
  const pinnedToday = kept.length === 0 ? [{ value: startTimeFor(today, loc), effectiveFrom: today }] : kept;
  // A deferred change skips tomorrow, so tomorrow must keep the start it had before the call, including a pending
  // value about to be dropped: tomorrow's start is the end of the day in progress, and `now` lies before it.
  const tomorrow = formatLocalDate(todayDays + 1);
  const base = deferred ? [...pinnedToday, { value: startTimeFor(tomorrow, loc), effectiveFrom: tomorrow }] : pinnedToday;
  return ok({ history: [...base, { value, effectiveFrom }], effectiveFrom });
}

/**
 * The calendar date of a minute of a business day: a wall-clock minute before the day's start (`start`, e.g. 04:00) falls
 * on the next calendar day, since the business day runs from its start to the next one's (B1462: an order at 12:04 AM on
 * business day 10/27 was taken on 10/28).
 */
export function calendarDateOf(date: BusinessDate, localMinute: number, start: LocalTime): LocalDate {
  return localMinute * 60 < localTimeToSeconds(start) ? addDays(date, 1) : date;
}
